/**
 * TicketSalesCampaignEditPage — `campaignEditPath(id)`. Edit a campaign,
 * switch it off, and see its change history. Saving sends only the fields
 * that changed.
 */
import type { TicketSalesCampaign } from '@bonsai/shared';
import { useParams } from 'react-router-dom';

import { TicketSalesCampaignApiError } from '../../api/ticket-sales-campaigns';
import { EditorPageNotice } from '../shared/editor-page/EditorPageNotice';
import { useJustCreated } from '../shared/editor-page/use-just-created';

import { campaignToForm, toPatchRequest } from './components/campaign-form';
import { TicketSalesCampaignEditor } from './components/TicketSalesCampaignEditor';
import { CAMPAIGNS_LIST_PATH } from './ticket-sales-campaigns-paths';
import { useTicketSalesCampaign, useUpdateTicketSalesCampaign } from './use-ticket-sales-campaigns';

const NOTICE = {
  listPath: CAMPAIGNS_LIST_PATH,
  listLabel: 'Campaigns',
  title: 'Edit campaign',
};

function loadProblem(error: unknown): string {
  if (error instanceof TicketSalesCampaignApiError && error.status === 404) {
    return 'This campaign doesn’t exist. It may have been removed, or the link is wrong.';
  }
  if (error instanceof TicketSalesCampaignApiError && error.status === 403) {
    return 'You don’t have permission to manage campaigns.';
  }
  return 'This campaign couldn’t be loaded. Refresh the page to try again.';
}

export default function TicketSalesCampaignEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const query = useTicketSalesCampaign(id);
  const update = useUpdateTicketSalesCampaign(id);
  const created = useJustCreated();

  if (query.isPending) {
    return <EditorPageNotice message="Loading the campaign…" {...NOTICE} />;
  }
  if (query.isError) {
    return <EditorPageNotice message={loadProblem(query.error)} {...NOTICE} problem />;
  }

  const campaign: TicketSalesCampaign = query.data;
  return (
    <TicketSalesCampaignEditor
      key={campaign.id}
      campaign={campaign}
      initial={campaignToForm(campaign)}
      initiallySaved={created}
      persist={(values, base) => {
        const patch = toPatchRequest(base, values);
        return patch ? update.mutateAsync(patch) : Promise.resolve(campaign);
      }}
    />
  );
}
